import { useState, useRef, useEffect } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Stethoscope, RotateCcw } from "lucide-react";

export default function AIChatWidget() {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis Dr CMES, votre assistant. Comment puis-je vous aider aujourd'hui ?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        message: text,
        session_id: sessionId,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("plan")) {
        setMessages(prev => [...prev, { role: "assistant", content: "Cette fonctionnalité nécessite un plan supérieur. Consultez la page Abonnement pour mettre à niveau." }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Désolé, une erreur s'est produite. Réessayez dans quelques instants." }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setSessionId(null);
    setMessages([{ role: "assistant", content: "Nouvelle conversation. Comment puis-je vous aider ?" }]);
  };

  if (!open) {
    return (
      <div className="fixed bottom-20 right-6 z-[9999] flex items-center gap-2">
        <span className="bg-white text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full shadow-md border border-gray-200 animate-pulse">
          Besoin d'aide ?
        </span>
        <button
          onClick={() => setOpen(true)}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="ai-chat-toggle"
        >
          <Stethoscope className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-[9999] w-[380px] h-[520px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 bg-white" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-white" />
          <div>
            <h3 className="text-white font-semibold text-sm">Dr CMES</h3>
            <p className="text-emerald-100 text-[10px]">Assistant IA - PHARMA PÉRIMÉS</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={resetChat} className="p-1.5 rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-all" data-testid="ai-chat-reset">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-all" data-testid="ai-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" data-testid="ai-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
              m.role === "user"
                ? "bg-emerald-600 text-white rounded-br-md"
                : "bg-white text-gray-800 rounded-bl-md border border-gray-200 shadow-sm"
            }`}>
              <div className="whitespace-pre-wrap">{m.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                <span className="text-xs text-gray-400">Dr CMES réfléchit...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-200 flex-shrink-0 bg-white">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMessage()}
            placeholder="Posez votre question..."
            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors"
            data-testid="ai-chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-500 transition-colors disabled:opacity-40"
            data-testid="ai-chat-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
